import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { NewsItem } from "@/types";

export default function HeroCarousel({ items }: { items: NewsItem[] }) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  const go = useCallback(
    (next: number) => {
      setIndex((prev) => (next + count) % count);
    },
    [count],
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const current = items[index];

  return (
    <section className="relative h-[560px] w-full overflow-hidden bg-secondary-950 md:h-[680px]">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.image}
            alt={`${item.title} 焦点图片`}
            title={`${item.title} - 风间由美官方资讯`}
            className={`h-full w-full object-top ${i === index ? "animate-slow-zoom" : ""}`}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/85 via-secondary-950/55 to-secondary-950/20" />

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1320px] flex-col justify-center px-4 pb-24 pt-28 md:px-6">
        <div key={current.id} className="max-w-2xl animate-fade-up">
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold tracking-wide text-background-50">
              焦点资讯
            </span>
            <span className="rounded-full border border-background-100/40 px-3 py-1 text-xs font-medium text-background-100">
              {current.category}
            </span>
            <span className="text-xs text-background-200">{current.date}</span>
          </div>
          <h2 className="font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {current.title}
          </h2>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            {current.summary}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              阅读全文
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/news"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-100/50 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
            >
              浏览全部资讯
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 z-10 flex w-full max-w-[1320px] -translate-x-1/2 items-center justify-between px-4 md:px-6">
        <div className="flex items-center gap-2">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 条焦点内容`}
              onClick={() => go(i)}
              className={`h-1.5 cursor-pointer rounded-full transition-all ${
                i === index ? "w-8 bg-primary-500" : "w-3 bg-background-100/50 hover:bg-background-100"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="上一条"
            onClick={() => go(index - 1)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-100/40 text-background-50 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            aria-label="下一条"
            onClick={() => go(index + 1)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-100/40 text-background-50 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>
    </section>
  );
}